import {
  ArrowUpRight,
  CalendarDays,
  Mic,
  Newspaper,
  Play,
  Radio,
  Users,
  Youtube,
} from 'lucide-react';
import type { ComponentType } from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { LiveNowStrip } from '../components/LiveNowStrip';
import { Topbar } from '../components/Topbar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Spinner } from '../components/ui/spinner';
import { useChannels, useNowPlaying } from '../lib/api/channels';
import { useEvents } from '../lib/api/events';
import { usePosts, useTrending } from '../lib/api/posts';
import { usePublicProgrammes } from '../lib/api/programmes';
import { useUsers } from '../lib/api/users';
import { useAuthStore } from '../lib/auth/store';
import { fmtRelative, todayLagosIso } from '../lib/date';
import { useLocaleStore } from '../lib/locales';
import { useRadioPlayerStore } from '../lib/radioPlayerStore';
import type { PublicChannel } from '../lib/types';
import { cn, formatNumber, plainText, truncate } from '../lib/utils';

export default function Dashboard(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const locale = useLocaleStore((s) => s.current);
  const isAdmin = user?.role === 'admin';

  const postsQ = usePosts({ locale, page: 1, pageSize: 6 });
  const trendingQ = useTrending('24h', locale);
  const programmesQ = usePublicProgrammes({ locale, page: 1, pageSize: 5 });
  const eventsQ = useEvents({ locale, page: 1, pageSize: 5 });
  const channelsQ = useChannels();
  const usersQ = useUsers({ page: 1, pageSize: 1 }, { enabled: isAdmin });

  // Posts published today in the current locale — drives the "Today" stat
  // card. Africa/Lagos is UTC+1 with no DST, so we can synthesise the
  // start-of-day instant directly without an Intl round-trip; the existing
  // `usePosts` endpoint accepts a `from` filter as ISO UTC.
  const startOfTodayLagosUtc = new Date(`${todayLagosIso()}T00:00:00+01:00`).toISOString();
  const postsTodayQ = usePosts({
    locale,
    from: startOfTodayLagosUtc,
    page: 1,
    pageSize: 1,
  });

  const today = new Date();
  const dateLabel = today.toLocaleDateString('en-US', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const editionTag = today
    .toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    .toUpperCase()
    .replace(/\s/g, ' ');

  return (
    <>
      <Topbar />

      {/* ─── Brand hero band ───────────────────────────────────── */}
      <section className="relative isolate overflow-hidden border-b border-brand-deep/40 bg-brand-radial text-white">
        {/* hairline grid texture for editorial atmosphere */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.07] bg-hairline-grid"
          style={{ backgroundSize: 'var(--grid-size) var(--grid-size)' }}
        />
        {/* tricolor stripe at the top of the band */}
        <div aria-hidden className="absolute inset-x-0 top-0 h-[3px] tricolor-spine [transform:rotate(180deg)]" />
        <div className="relative px-6 pt-7 pb-8 lg:pt-10 lg:pb-10">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div className="max-w-2xl">
              <p className="eyebrow-on-brand">
                <span className="live-dot" /> Newsroom Desk · Edition {editionTag}
              </p>
              <h1 className="mt-3 font-heading text-3xl font-semibold leading-[1.1] tracking-tight md:text-[36px]">
                Welcome back
                {user?.name ? (
                  <>
                    , <span className="text-white/85">{user.name.split(' ')[0]}</span>
                  </>
                ) : null}
                <span className="text-white/55">.</span>
              </h1>
              <p className="mt-2 max-w-xl text-[14px] leading-relaxed text-white/70">
                {dateLabel} — broadcasting in {locale.toUpperCase()}.
                {' '}Here's what's running across the network today.
              </p>
            </div>
            <div className="flex shrink-0 flex-col items-start gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 backdrop-blur">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/55">
                Locale · Role
              </p>
              <p className="font-heading text-base font-semibold tracking-tight">
                {locale.toUpperCase()}
                <span className="mx-2 text-white/30">/</span>
                <span className="text-white/85">{user?.role?.toUpperCase() ?? '—'}</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Stat grid ─────────────────────────────────────────── */}
      <div className="-mt-6 px-6">
        <div className="stagger grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            icon={Newspaper}
            label="Posts"
            sublabel={`In ${locale.toUpperCase()}`}
            value={postsQ.data?.total}
            href="/posts"
            loading={postsQ.isLoading}
          />
          <StatCard
            icon={Mic}
            label="Programmes"
            sublabel="Active roster"
            value={programmesQ.data?.total}
            href={isAdmin ? '/programmes' : '/posts'}
            loading={programmesQ.isLoading}
          />
          <StatCard
            icon={CalendarDays}
            label="Today"
            sublabel={`Posts in ${locale.toUpperCase()}`}
            value={postsTodayQ.data?.total}
            href="/posts"
            loading={postsTodayQ.isLoading}
          />
          <StatCard
            icon={Users}
            label="Users"
            sublabel="Total registered"
            value={usersQ.data?.total}
            href={isAdmin ? '/users' : '/posts'}
            loading={usersQ.isLoading}
            adminOnly={!isAdmin}
          />
        </div>
      </div>

      <div className="space-y-5 px-6 pb-12 pt-6">
        <LiveNowStrip locale={locale} />

        {/* ─── Editorial split: latest wire + trending ranking ── */}
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          <Card className="lg:col-span-2">
            <CardHeader>
              <p className="eyebrow">The Wire</p>
              <CardTitle>Latest posts</CardTitle>
              <CardDescription>
                Most recent items in {locale.toUpperCase()}, top of the queue.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {postsQ.isLoading ? (
                <div className="p-8">
                  <Spinner label="Loading…" />
                </div>
              ) : Array.isArray(postsQ.data?.items) && postsQ.data.items.length > 0 ? (
                <ul className="divide-y divide-line-hair">
                  {postsQ.data.items.map((p) => (
                    <li key={p.id}>
                      <Link
                        to={`/posts/${p.slug}`}
                        className="group flex items-start gap-4 px-5 py-3.5 transition-colors hover:bg-surface-tinted"
                      >
                        {p.featuredImage?.url ? (
                          <img
                            src={p.featuredImage.url}
                            alt={p.featuredImage.alt ?? ''}
                            className="h-14 w-20 shrink-0 rounded-md object-cover ring-1 ring-line"
                            loading="lazy"
                          />
                        ) : (
                          <div className="grid h-14 w-20 shrink-0 place-items-center rounded-md border border-line bg-surface-tinted text-ink-subtle">
                            <Newspaper className="h-4 w-4" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="line-clamp-2 text-[14px] font-medium leading-snug text-ink group-hover:text-brand">
                            {plainText(p.title)}
                          </p>
                          <p className="mt-1 flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-[0.1em] text-ink-subtle">
                            <span className="truncate">{p.author?.name ?? 'Unknown'}</span>
                            <span aria-hidden className="h-1 w-1 rounded-full bg-line-strong" />
                            <span className="shrink-0 normal-case tracking-normal">
                              {fmtRelative(p.publishedAt)}
                            </span>
                          </p>
                        </div>
                        <ArrowUpRight className="mt-1 h-4 w-4 shrink-0 text-ink-subtle opacity-0 transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:opacity-100 group-hover:text-brand" />
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="px-5 py-8 text-sm text-ink-muted">No posts ingested yet.</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <p className="eyebrow">Top of the Hour</p>
              <CardTitle>Trending today</CardTitle>
              <CardDescription>Top items by reactions and bookmarks.</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {trendingQ.isLoading ? (
                <div className="p-8">
                  <Spinner label="Loading…" />
                </div>
              ) : Array.isArray(trendingQ.data?.items) && trendingQ.data.items.length > 0 ? (
                <ol className="divide-y divide-line-hair">
                  {trendingQ.data.items.map((t, i) => (
                    <li key={t.id}>
                      <Link
                        to={`/posts/${t.slug}`}
                        className="group flex items-start gap-3.5 px-5 py-3 transition-colors hover:bg-surface-tinted"
                      >
                        <span
                          className={cn(
                            'num mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-md font-heading text-[12px] font-semibold tabular-nums tracking-tight',
                            i === 0
                              ? 'bg-brand text-white'
                              : i < 3
                                ? 'bg-brand-soft text-brand'
                                : 'border border-line text-ink-muted',
                          )}
                        >
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        <span className="line-clamp-2 text-[13.5px] leading-snug text-ink group-hover:text-brand">
                          {truncate(plainText(t.title), 90)}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="px-5 py-8 text-sm text-ink-muted">No trending data yet.</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* ─── Admin row: channels + events ─────────────────────── */}
        {isAdmin ? (
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <p className="eyebrow">On Air</p>
                <CardTitle>Channels & now-playing</CardTitle>
                <CardDescription>Live status of broadcast streams.</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {channelsQ.isLoading ? (
                  <div className="p-8">
                    <Spinner label="Loading…" />
                  </div>
                ) : Array.isArray(channelsQ.data?.items) && channelsQ.data.items.length > 0 ? (
                  <ul className="divide-y divide-line-hair">
                    {channelsQ.data.items.map((ch) => (
                      <NowPlayingRow key={ch.id} channel={ch} />
                    ))}
                  </ul>
                ) : (
                  <p className="px-5 py-8 text-sm text-ink-muted">No channels yet.</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <p className="eyebrow">Calendar</p>
                <CardTitle>Upcoming events</CardTitle>
                <CardDescription>Next 5 events in {locale.toUpperCase()}.</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {eventsQ.isLoading ? (
                  <div className="p-8">
                    <Spinner label="Loading…" />
                  </div>
                ) : Array.isArray(eventsQ.data?.items) && eventsQ.data.items.length > 0 ? (
                  <ul className="divide-y divide-line-hair">
                    {eventsQ.data.items.map((ev) => {
                      const start = ev.startAt ? new Date(ev.startAt) : null;
                      return (
                        <li key={ev.id}>
                          <Link
                            to={`/events/${ev.slug}`}
                            className="group flex items-start gap-4 px-5 py-3.5 transition-colors hover:bg-surface-tinted"
                          >
                            {start ? (
                              <div className="flex w-12 shrink-0 flex-col items-center rounded-md border border-line bg-surface-tinted py-1.5 text-center">
                                <span className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.14em] text-brand">
                                  {start.toLocaleString('en-US', { month: 'short' })}
                                </span>
                                <span className="font-heading text-[18px] font-semibold leading-none text-ink num">
                                  {start.getDate()}
                                </span>
                              </div>
                            ) : null}
                            <div className="min-w-0 flex-1">
                              <p className="line-clamp-2 text-[14px] font-medium leading-snug text-ink group-hover:text-brand">
                                {ev.title}
                              </p>
                              <p className="mt-1 font-mono text-[10.5px] uppercase tracking-[0.1em] text-ink-subtle">
                                <span className="normal-case tracking-normal">
                                  {fmtRelative(ev.startAt)}
                                </span>
                                <span aria-hidden className="mx-1.5">·</span>
                                <span>{ev.venue ?? 'Venue TBA'}</span>
                              </p>
                            </div>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="px-5 py-8 text-sm text-ink-muted">Nothing on the calendar.</p>
                )}
              </CardContent>
            </Card>
          </div>
        ) : null}
      </div>
    </>
  );
}

interface StatCardProps {
  icon: ComponentType<{ className?: string }>;
  label: string;
  sublabel?: string;
  value: number | undefined;
  href: string;
  loading?: boolean;
  adminOnly?: boolean;
}

function StatCard({
  icon: Icon,
  label,
  sublabel,
  value,
  href,
  loading,
  adminOnly,
}: StatCardProps): JSX.Element {
  return (
    <Link
      to={href}
      className={cn(
        'group relative flex animate-fade-up flex-col justify-between gap-5 overflow-hidden rounded-xl border border-line bg-surface p-5 shadow-card transition-all duration-200 hover:-translate-y-0.5 hover:border-brand/40 hover:shadow-elevated',
        adminOnly && 'pointer-events-none opacity-60',
      )}
    >
      {/* accent rule on hover */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[3px] origin-left scale-x-0 bg-brand transition-transform duration-300 ease-out group-hover:scale-x-100"
      />
      <div className="flex items-start justify-between">
        <div>
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-ink-subtle">
            {label}
          </p>
          {sublabel ? (
            <p className="mt-1 text-[11.5px] text-ink-subtle">{sublabel}</p>
          ) : null}
        </div>
        <span className="grid h-9 w-9 place-items-center rounded-lg bg-brand-soft text-brand transition-colors group-hover:bg-brand group-hover:text-white">
          <Icon className="h-[18px] w-[18px]" />
        </span>
      </div>
      <div className="flex items-end justify-between gap-3">
        <p className="num font-heading text-[34px] font-semibold leading-none tracking-tight text-ink">
          {loading ? (
            <span className="inline-block h-7 w-16 animate-pulse rounded bg-surface-muted" />
          ) : (
            formatNumber(value)
          )}
        </p>
        <ArrowUpRight className="mb-1 h-3.5 w-3.5 shrink-0 text-ink-subtle transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-brand" />
      </div>
    </Link>
  );
}

function NowPlayingRow({ channel }: { channel: PublicChannel }): JSX.Element {
  const locale = useLocaleStore((s) => s.current);
  const np = useNowPlaying(channel.slug, locale);
  const radio = useRadioPlayerStore();
  const [videoOpen, setVideoOpen] = useState(false);

  const online = np.data?.stream?.online;
  const isPlaying = radio.playing && radio.channelSlug === channel.slug;

  const handleRadio = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!channel.streamUrl) return;
    // Pass the raw upstream — the store wraps with the current baseUrl when
    // setting audio.src. See radioPlayerStore.ts persist version=2.
    radio.setChannel(channel.slug, channel.name, channel.streamUrl);
  };

  return (
    <>
      <li className="flex items-center gap-3 px-5 py-3.5">
        {/* Radio play button */}
        <button
          type="button"
          onClick={handleRadio}
          disabled={!channel.streamUrl}
          title={isPlaying ? 'Pause radio' : 'Play radio stream'}
          className={cn(
            'mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg transition-colors disabled:opacity-40',
            isPlaying
              ? 'bg-brand text-white'
              : online
                ? 'bg-brand-soft text-brand hover:bg-brand hover:text-white'
                : 'bg-surface-muted text-ink-subtle',
          )}
        >
          {isPlaying ? <Radio className="h-4 w-4 animate-pulse" /> : <Play className="h-4 w-4 translate-x-px" />}
        </button>

        {/* Channel info — links to now-playing */}
        <Link to="/now-playing" className="group min-w-0 flex-1">
          <p className="text-[13.5px] font-medium leading-tight text-ink group-hover:text-brand">
            {channel.name}
          </p>
          <p className="line-clamp-1 mt-0.5 font-mono text-[10.5px] text-ink-subtle">
            <span className="normal-case tracking-normal">
              Now: {np.data?.current?.title ?? '—'}
              {np.data?.next ? ` · Next: ${np.data.next.title}` : ''}
            </span>
          </p>
        </Link>

        {/* Actions: YouTube button + status badge */}
        <div className="flex shrink-0 flex-col items-end gap-1">
          <div className="flex items-center gap-1.5">
            {channel.youtubeLiveStreamId ? (
              <button
                type="button"
                onClick={() => setVideoOpen(true)}
                title="Watch live stream"
                className="grid h-6 w-6 place-items-center rounded text-ink-subtle transition-colors hover:bg-red-50 hover:text-red-600"
              >
                <Youtube className="h-3.5 w-3.5" />
              </button>
            ) : null}
            <span
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-[0.1em]',
                online ? 'bg-live/10 text-live' : 'bg-surface-muted text-ink-muted',
              )}
            >
              {online ? <span className="live-dot" /> : <span className="h-1.5 w-1.5 rounded-full bg-ink-subtle" />}
              {online ? 'On air' : 'Offline'}
            </span>
          </div>
          {np.data?.stream?.listeners !== null && np.data?.stream?.listeners !== undefined ? (
            <p className="num font-mono text-[10.5px] tracking-tight text-ink-subtle">
              {formatNumber(np.data.stream.listeners)} listeners
            </p>
          ) : null}
        </div>
      </li>

      {/* Live video modal */}
      {channel.youtubeLiveStreamId ? (
        <Dialog open={videoOpen} onOpenChange={setVideoOpen}>
          <DialogContent size="xl" className="p-0 overflow-hidden">
            <DialogHeader className="px-5 pt-5 pb-3">
              <DialogTitle>{channel.name} — Live</DialogTitle>
              {np.data?.current ? (
                <p className="text-sm text-ink-muted">
                  On air: <span className="font-medium text-ink">{np.data.current.title}</span>
                  <Link
                    to={`/programmes/${encodeURIComponent(np.data.current.programmeSlug)}`}
                    onClick={() => setVideoOpen(false)}
                    className="ml-2 text-brand hover:underline text-xs"
                  >
                    Programme settings ↗
                  </Link>
                </p>
              ) : null}
            </DialogHeader>
            <div className="relative w-full pb-[56.25%] bg-black">
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${channel.youtubeLiveStreamId}?autoplay=1&rel=0`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={`${channel.name} live stream`}
                className="absolute inset-0 h-full w-full border-0"
              />
            </div>
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
}

